/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as CertificateIcon,
} from '@mui/icons-material'
import Subheader from 'src/v3/components/subheader'
import useSession from 'src/authentication/hooks/use-session'
import CertificateBuilder from 'src/v3/components/certificate-builder/certificate-builder'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectUseEvents } from 'src/v3/settings/settings.slice'
import {
  fetchLinkedServices,
  selectLinkedServicesByIds,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
  selectActiveLinkedService,
} from '../manage.slice'
import BasicInfo from './basic-info'
import AssignmentCompletionThreshold from './assignment-completion-threshold'
import ServiceNameEditor from './service-name-editor'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let session = useSession()
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedServices = useAppSelector(
    selectLinkedServicesByIds(session?.linkedServices ?? []),
  )
  let selectedLinkedService = linkedServices.find(
    (service) => service.id === activeLinkedService,
  )
  let [showCertificateBuilder, setShowCertificateBuilder] = useState(false)
  let useEvents = useAppSelector(selectUseEvents)

  React.useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus, dispatch])

  let handleLinkedServiceChange = (value: string) => {
    dispatch(activeLinkedServiceChanged(value))
  }

  let handleCertificateComplete = (blob: Blob) => {
    // Handle the completed certificate blob
    // You can save it to server, download, etc.
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'certificate.png'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setShowCertificateBuilder(false)
  }

  return (
    <>
      <Subheader title="Linked Service Management" />

      {/* Service Selector */}
      <Paper elevation={1} sx={{ p: 3, mb: 6 }}>
        <FormControl fullWidth>
          <InputLabel id="linked-service-select-label">
            Select Linked Service
          </InputLabel>
          <Select
            labelId="linked-service-select-label"
            value={activeLinkedService || ''}
            label="Select Linked Service"
            onChange={(e) => handleLinkedServiceChange(e.target.value)}
            sx={{ minHeight: 56 }}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 32, height: 32 }}>LS</Avatar>
                <Typography>No service selected</Typography>
              </Box>
            </MenuItem>
            {linkedServices.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${service.imageName}`}
                    sx={{ width: 32, height: 32 }}
                  >
                    {service.name?.charAt(0) || 'LS'}
                  </Avatar>
                  <Typography>{service.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Management Cards */}
      <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
        <Card elevation={2}>
          <CardContent sx={{ minHeight: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Basic Settings
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure basic information and service details.
            </Typography>

            {/* Service Name Editing */}
            <ServiceNameEditor />
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ minHeight: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssignmentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assignment Thresholds
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set completion thresholds and requirements for assignments.
            </Typography>

            {/* Assignment Completion Threshold */}
            <AssignmentCompletionThreshold />
          </CardContent>
        </Card>

        {useEvents && (
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CertificateIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Certificate Designer
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create custom certificates with your branding.
              </Typography>

              {selectedLinkedService?.certificateImageName && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Current Certificate Design:
                  </Typography>
                  <img
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${selectedLinkedService.certificateImageName.replace('originals', 'resized')}`}
                    alt="Certificate Design"
                    style={{
                      maxWidth: '100%',
                      objectFit: 'contain',
                      border: '1px solid #ccc',
                      borderRadius: 8,
                    }}
                  />
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowCertificateBuilder(true)}
                sx={{ textTransform: 'none' }}
              >
                {selectedLinkedService?.certificateImageName
                  ? 'Edit'
                  : 'Design'}{' '}
                Certificate
              </Button>
            </CardActions>
          </Card>
        )}
        <BasicInfo />
      </div>

      {/* Service Logo Section */}

      {/* Certificate Builder Modal */}
      {showCertificateBuilder && (
        <CertificateBuilder
          onComplete={handleCertificateComplete}
          onCancel={() => setShowCertificateBuilder(false)}
          linkedServiceId={activeLinkedService}
        />
      )}
    </>
  )
}
